import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  QueryList,
  ViewChildren,
  ViewChild,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
} from '@angular/core';
import { Subject } from 'rxjs';
import { BoxService } from '@app/services/boxes/box.service';
import { FulfillService } from '../../fulfill.service';
import { FulfillItem, PanelState, PickItem } from '../../utils/models';
import { SwitchOrderPackageComponent } from '@pages/workspace/fulfill/components/fulfill-side-panel-order-items/components/switch-order-package/switch-order-package.component';
import { MatDialog } from '@angular/material/dialog';
import { PdqToastComponent } from '@storybook/components/toast/toast.component';
import { ToastService } from '@storybook/services/toast/toast.service';
import { OrderUpdateCase } from '../../utils/models';
import { takeUntil } from 'rxjs/operators';
import { FulfillMsgDialogComponent } from './components/fulfill-msg-dialog/fulfill-msg-dialog.component';
import { TranslatedFulfillmentStatus } from '@pdqdev/pdq-shared/dist/OrderData';
import { InternalOrderItemStatus, OrderItemStatus } from '@pdqdev/pdq-shared/dist/OrderItemData';
import { ActivatedRoute } from '@angular/router';
import { FulfillScanPackageDialog } from '../../dialogs/fulfill-scan-package-dialog/fulfill-scan-package-dialog';
import { FulfillmentStatus } from '@app/services/order/order.model';
import { getOrderSplitIndicesString } from '@utils/order-split-utils';
import { FulfillSidePanelOrderItemCardComponent } from '../fulfill-side-panel-order-item-card/fulfill-side-panel-order-item-card.component';

@Component({
  selector: 'fulfill-side-panel-order-items',
  templateUrl: './fulfill-side-panel-order-items.component.html',
  styleUrls: ['./fulfill-side-panel-order-items.component.scss'],
  standalone: false,
})
export class FulfillSidePanelOrderItemsComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @ViewChildren('itemCard') itemCards: QueryList<FulfillSidePanelOrderItemCardComponent>;
  @ViewChild('scan') scanInput;
  @ViewChild('scanAccessoryInput') scanAccessoryInput;
  @ViewChild('scanMultiItemsSkuInput') scanMultiItemsSkuInput;
  @Input() order;
  @Input() isPickAndPackFocus;
  private destroy$ = new Subject();
  readonly InternalOrderItemStatus = InternalOrderItemStatus;

  orderItems;
  disableActions: boolean = false;
  scannedSku;
  showOrderPackage;
  packageName;
  packages$;
  activeBatch;
  carrierSupportsSwitchPackage: boolean = false;
  fulfilledOrderItems;
  unfulfilledOrderItems;
  fulfilledOrderItemsQuantity = 0;
  unfulfilledOrderItemsQuantity = 0;
  isPickingMode: boolean = false;
  packageScanMode;
  accessoryItemSku;
  scannedAccessory;
  isOnlyAccessoryItem;
  enableAccessory;
  isMultiItemPending;
  multiItemScanned: { [sku: string]: number } = {};
  scannedMultiItemSku;
  enableMultiItem;
  orderSplitIndices;
  isDisableManualFulfillment: boolean = false;
  constructor(
    private fs: FulfillService,
    private dialog: MatDialog,
    private boxService: BoxService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private fulfillService: FulfillService,
  ) {}

  ngOnInit(): void {
    this.resetUpdateTypes();
    this.activeBatch = this.fs.activeBatch;
    this.carrierSupportsSwitchPackage = this.activeBatch?.selectedCarrier?.type?.custom_fields?.supportsSwitchPackage;
    this.isPickingMode = this.fs.isPickingMode;
    this.initOrderPackageInfo();
    this.orderSplitIndices = getOrderSplitIndicesString(this.order, 'view');
    this.packageScanMode = this.fs.shopMetadata.isPackageScanFulfill;
    if (this.packageScanMode) {
      this.initPackageScanMode();
    }
    this.isDisableManualFulfillment = this.fs.isDisableManualFulfillmentAndPickNPackRole();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.order.currentValue) {
      this.disableActions =
        changes.order.currentValue.updateType?.includes(OrderUpdateCase.LINE_ITEMS_CHANGED) ||
        changes.order.currentValue.updateTypeV2?.includes(OrderUpdateCase.LINE_ITEMS_LOCATION_CHANGED);
      this.orderItems = changes.order.currentValue?.orderItems;
      this.orderSplitIndices = getOrderSplitIndicesString(changes.order.currentValue, 'view');
      this.initOrderItems();
    }
  }

  private initPackageScanMode(): void {
    const accessoryProperty = 'bundled: true';
    const accessoryItem = this.order.orderItems.find(
      (o) => o.properties.includes(accessoryProperty) || o.sku.includes('MXT-WS'),
    );
    const itemsQuantity = this.orderItems.reduce((acc, item) => acc + item.quantity, 0);
    if (
      accessoryItem &&
      accessoryItem.fulfillmentStatus !== FulfillmentStatus.FULFILLED &&
      (itemsQuantity == 1 || itemsQuantity == 2)
    ) {
      const item = this.order.orderItems.find((o) => o.properties.includes(`Accessory: ${accessoryItem.sku}`));
      if (item || itemsQuantity == 1) {
        /* Current logic flow:
         * - Single non-accessory unfulfilled item: Enable package scan mode only
         * - Single accessory unfulfilled item: Enable accessory scan mode only
         * - Orders with 2 items: (Plant + Accessory) - Enable accessory scan and after enable package scan
         * - Orders with accessory items or multiple items: Enable SKU/accessory scan first
         */
        if (!item) {
          this.isOnlyAccessoryItem = true;
        } else if (itemsQuantity == 1) {
          this.isMultiItemPending = true;
          this.enableMultiItem = true;
        }
      }
      if (itemsQuantity == 2) {
        this.accessoryItemSku = accessoryItem.sku;
        this.enableAccessory = true;
        this.unfulfilledOrderItems?.sort((a, b) => (a.sku === this.accessoryItemSku ? -1 : 1));
      }

      // Note: The following logic has been temporarily disabled per merchant request.
      // For reference to the previous implementation, see: https://miro.com/app/board/uXjVLdUOqKw=/

      // this.accessoryItemSku = accessoryItem.sku;
      // this.enableAccessory = true;
      // this.unfulfilledOrderItems?.sort((a, b) => (a.sku === this.accessoryItemSku ? -1 : 1));
    } else if (this.orderItems.length > 1) {
      this.order.orderItems.map((orderItem) => {
        if (
          orderItem.fulfillmentStatus === TranslatedFulfillmentStatus.FULFILLED ||
          orderItem.fulfillmentStatus === TranslatedFulfillmentStatus.PARTIALLY_FULFILLED
        ) {
          this.multiItemScanned[orderItem?.sku] = orderItem?.quantity - orderItem?.fulfillableQuantity;
        }
      });
      this.isMultiItemPending = true;
      this.enableMultiItem = true;
    }
  }

  handleOrdersUpdated(updatedOrders: { id: string; pendingUpdates: any[] }[]) {
    if (updatedOrders?.length) {
      const pendingUpdates = updatedOrders[0].pendingUpdates ?? [];
      this.order.pendingUpdates = pendingUpdates;
      this.fulfillService.emitRefreshBatchInList({ batchId: this.activeBatch.id });
      this.fulfillService.updateOrderInBatch(this.activeBatch.id, this.order);
    }
  }
  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.onScanTriggerAutoFulfill();
    this.onPrintTriggerFulfillment();
    this.assignIdsToItemCards();
  }

  onPrintTriggerFulfillment() {
    this.fs.onPrintTriggerFulfillment.pipe(takeUntil(this.destroy$)).subscribe((orderId: string) => {
      if (orderId === this.order.id) {
        this.fulfillAll();
      }
    });
  }

  onScanTriggerAutoFulfill() {
    const fromScan = this.route.snapshot.queryParams['from'] === PanelState.SCAN;
    if (fromScan && this.fs.shopMetadata?.onScanTriggerFulfillment) {
      this.fulfillAll();
    }
  }
  assignIdsToItemCards() {
    this.itemCards?.forEach((itemCard, index) => {
      itemCard.id = `item-card-${index}`;
    });
  }

  resetUpdateTypes() {
    this.fs.onResetUpdateType$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.disableActions = false;
    });
  }

  private getFulfillmentItemFromOrderItem(item: any) {
    return {
      id: item?.externalId,
      pdqId: item?.id,
      fulfillmentStatus: item?.fulfillmentStatus,
      fulfillmentId: item?.fulfillmentExternalId,
      quantity: item?.quantity,
      quantityFulfilled: item.quantity - item.fulfillableQuantity,
      product_external_id: item.product.externalIdNumber,
    };
  }
  fulfillAll(autoPrint: boolean = false) {
    if (!this.fs.checkAbilityToFulfill()) return;
    const itemsToFulfill: FulfillItem[] = this.orderItems.map((item) => this.getFulfillmentItemFromOrderItem(item));
    if (this.accessoryItemSku || this.isOnlyAccessoryItem) {
      this.scanAccessoryInput.focus();
    } else {
      this.scanInput.focus();
    }
    this.fs.updateItemsToFulfill(itemsToFulfill);
    this.fs.fulfillItems(autoPrint);
  }
  pickAll() {
    if (!this.fs.checkAbilityToFulfill()) return;
    const itemsToPick: PickItem[] = this.orderItems.map((item) => {
      return { pdqId: item?.id, internalStatus: item?.internalStatus, quantity: item?.quantity };
    });
    this.fs.updateItemsToPick(itemsToPick, true);
  }

  get hasPendingUpdates() {
    return this.order?.pendingUpdates?.filter((update) => !update.dismissedBy)?.length > 0;
  }

  checkAutoFulfill() {
    if (!this.fs.checkAbilityToFulfill() || this.isMultiItemPending || this.isOnlyOneAccessoryItem()) return;
    const fulfillmentArray = this.fs.fulfillmentArray;
    const currentCountersTotal = fulfillmentArray.reduce(
      (acc, next: any) => acc + next.quantity - (next.quantityFulfilled ?? 0),
      0,
    );
    this.fs.checkAutoFulfillment(currentCountersTotal);
  }

  isOnlyOneAccessoryItem() {
    const itemsQuantity = this.orderItems.reduce((acc, item) => acc + item.quantity, 0);
    return itemsQuantity == 1 && this.orderItems[0].sku == this.accessoryItemSku;
  }

  checkAutoPick() {
    if (!this.fs.checkAbilityToFulfill()) return;
    const currentCountersTotal = this.itemCards.reduce((acc, next: any) => acc + next.pickingCounter, 0);
    this.fs.checkAutoPickment(currentCountersTotal);
  }

  scanSku() {
    this.scannedSku = this.scanInput.input.nativeElement.value?.toLowerCase();
    if (!this.scannedSku.length) return;
    if (this.packageScanMode) {
      this.openPackageScanDialog();
    } else {
      this.scanItem(this.scannedSku, false, this.scanInput);
    }
    this.scannedSku = '';
    this.scanInput.input.nativeElement.value = '';
  }

  scanItem(scannedItem: string, isAccessory: boolean, elementToFocus) {
    try {
      const matchingOrderItem: any = this.getMatchingItem(scannedItem);
      if (matchingOrderItem) {
        if (isAccessory && matchingOrderItem.orderItem?.sku !== this.accessoryItemSku) {
          this.displayFulfillMsgDialog(
            ['This product’s SKU isn’t accessory item.', 'Please take another look and try again.'],
            elementToFocus,
          );
          return null;
        }
        this.isPickingMode
          ? this.updateItemCounterForPicking(matchingOrderItem, elementToFocus)
          : this.updateItemCounter(matchingOrderItem, elementToFocus);
        return matchingOrderItem;
      } else {
        this.displaySkuMsgDialog(elementToFocus);
      }
    } catch (e) {
      this.displaySkuMsgDialog(elementToFocus);
      console.warn('Failed to match barcode/sku', e);
    }
  }

  displaySkuMsgDialog(elementToFocus) {
    this.displayFulfillMsgDialog(
      ['This product’s SKU isn’t listed in this order.', 'Please take another look and try again.'],
      elementToFocus,
    );
  }

  displayFulfillMsgDialog(content: string[], elementTofocus) {
    const fulfillMsgDialog = this.dialog.open(FulfillMsgDialogComponent, {
      data: {
        content,
      },
      width: '612px',
      height: '208px',
      panelClass: 'overflow-visible-class',
      disableClose: true,
      autoFocus: false,
    });
    fulfillMsgDialog.afterClosed().subscribe(() => {
      elementTofocus.focus();
    });
  }

  getMatchingItem(scannedItem: string) {
    const isMultiParcel = this.fs.shopMetadata.isMultiParcelSupport;
    return isMultiParcel ? this.findScannedItemInMultiParcel(scannedItem) : this.findScannedItem(scannedItem);
  }

  findScannedItem(scannedItem: string, itemFulfilled?: boolean) {
    const matchingItems = this.itemCards.filter((o: any) => {
      const matchBarcode = this.compareWithBarcode(o.orderItem?.variant?.barcode, scannedItem);
      const matchSku = o.orderItem?.sku?.toLowerCase() == scannedItem;
      if (itemFulfilled) {
        return (matchSku || matchBarcode) && o.itemFulfilled;
      }

      return matchSku || matchBarcode;
    });
    return matchingItems.length
      ? (matchingItems.find((item) => this.isFulfillableItem(item)) ?? matchingItems[0])
      : undefined;
  }

  findScannedItemInMultiParcel(scannedItem: string) {
    return this.itemCards.find((o: any) => {
      const matchBarcode = this.compareWithBarcode(o.orderItem?.variant?.barcode, scannedItem);
      const matchSku = o.orderItem?.sku?.toLowerCase() == scannedItem;

      return (matchSku || matchBarcode) && o.counter < 1;
    });
  }

  isFulfillableItem(item) {
    return item.orderItem.fulfillableQuantity > 0 && item.counter < item.orderItem.quantity;
  }

  updateItemCounter(item, elementToFocus) {
    if (this.isFulfillableItem(item)) {
      const newCounter = item.counter ? item.counter + 1 : 1;
      item.counterRef.value = newCounter;
      item.updateFulfillmentQuantity(newCounter);
    } else {
      this.displayFulfillMsgDialog(
        ['You’ve already fulfilled this product so it wasn’t counted this time.'],
        elementToFocus,
      );
    }
  }

  updateItemCounterForPicking(item, elementToFocus) {
    if (
      (item.orderItem.pickableQuantity ?? item.orderItem.quantity) > 0 &&
      item.pickingCounter < item.orderItem.quantity
    ) {
      const newCounter = item.pickingCounter ? item.pickingCounter + 1 : 1;
      item.pickingCounterRef.value = newCounter;
      item.updatePickmentQuantity(newCounter);
    } else {
      this.displayFulfillMsgDialog(
        ['You’ve already picked this product so it wasn’t counted this time.'],
        elementToFocus,
      );
    }
  }

  initOrderPackageInfo(): void {
    this.showOrderPackage =
      !this.fs.shopMetadata?.isMultiParcelSupport &&
      this.fs.shopMetadata?.supportsMeasuresValidation &&
      this.order?.box;
    this.packageName = this.boxService.utils.getBoxName(this.order.box);
  }

  switchPackage(): void {
    const switchDialogRef = this.dialog.open(SwitchOrderPackageComponent, {
      panelClass: 'overflow-visible-class',
      data: { order: this.order },
      disableClose: true,
    });

    switchDialogRef.afterClosed().subscribe((event: { success: boolean; updatedBox: any }) => {
      if (event && event.success) {
        this.packageName = this.boxService.utils.getBoxName(event?.updatedBox);
        this.toastService.show(PdqToastComponent, { message: 'Package details updated', mode: 'info', duration: 3000 });
      } else if (event && event.success == false) {
        this.fs.setActiveBatch({
          ...this.activeBatch,
          errors: ['Order was detached from batch and moved back to assign'],
        });
        this.fs.setPanelState(PanelState.BATCH);
      }
    });
  }

  compareWithBarcode(barcode, scannedItem: string) {
    return barcode?.length > 0 && scannedItem.includes(barcode?.toLowerCase());
  }

  initOrderItems() {
    this.unfulfilledOrderItems = [];
    this.fulfilledOrderItems = [];
    this.unfulfilledOrderItemsQuantity = 0;
    this.fulfilledOrderItemsQuantity = 0;
    if (!this.order) {
      return;
    }
    if (
      !this.order.shop.platform ||
      this.order.shop.platform === 'shopify' ||
      this.order.shop.platform === 'wix' ||
      this.order.shop.platform === 'orderhive' ||
      this.order.shop.platform === 'custom'
    ) {
      this.orderItems.forEach((orderItem) => {
        if (orderItem.fulfillmentStatus === TranslatedFulfillmentStatus.FULFILLED) {
          this.fulfilledOrderItems.push(orderItem);
          this.fulfilledOrderItemsQuantity += orderItem.quantity;
        } else if (orderItem.fulfillmentStatus === TranslatedFulfillmentStatus.NOT_FULFILLED) {
          const holdedOrderItemQuantity = this.order.locationOrderItems.reduce((sum, locationItem) => {
            if (locationItem.status == OrderItemStatus.HOLD && locationItem.orderItem?.id === orderItem.id) {
              sum += locationItem.quantity;
            }
            return sum;
          }, 0);
          this.unfulfilledOrderItems.push({ ...orderItem, holdQuantity: holdedOrderItemQuantity });
          this.unfulfilledOrderItemsQuantity += orderItem.quantity;
        } else {
          const holdedOrderItemQuantity = this.order.locationOrderItems.reduce((sum, locationItem) => {
            if (locationItem.status == OrderItemStatus.HOLD && locationItem.orderItem?.id === orderItem.id) {
              sum += locationItem.quantity;
            }
            return sum;
          }, 0);
          const unfulfilledOrderItemsQuantity = orderItem.fulfillableQuantity + holdedOrderItemQuantity;
          this.unfulfilledOrderItems.push({
            ...orderItem,
            sourceQuantity: orderItem.quantity,
            holdQuantity: holdedOrderItemQuantity,
            quantity: unfulfilledOrderItemsQuantity,
          });
          this.unfulfilledOrderItemsQuantity += unfulfilledOrderItemsQuantity;

          const fulfilledOrderItemQuantity = orderItem.quantity - unfulfilledOrderItemsQuantity;
          // ensure that current item is fulfilled
          if (fulfilledOrderItemQuantity > 0) {
            this.fulfilledOrderItems.push({ ...orderItem, quantity: fulfilledOrderItemQuantity });
            this.fulfilledOrderItemsQuantity += fulfilledOrderItemQuantity;
          }
        }
      });
      this.order.digitalItems.forEach((digitalItem) => {
        if (digitalItem.fulfillmentStatus === TranslatedFulfillmentStatus.FULFILLED) {
          this.fulfilledOrderItems.push(digitalItem);
          this.fulfilledOrderItemsQuantity += digitalItem.quantity;
        } else {
          this.unfulfilledOrderItems.push(digitalItem);
          this.unfulfilledOrderItemsQuantity += digitalItem.quantity;
        }
      });
    } else {
      let quantity = this.orderItems.reduce((quantity, item: any) => quantity + item.quantity, 0);
      quantity += this.order.digitalItems.reduce((quantity, item: any) => quantity + item.quantity, 0);
      if (this.order.fulfillmentStatus === TranslatedFulfillmentStatus.FULFILLED) {
        this.fulfilledOrderItems = this.orderItems.concat(this.order.digitalItems ? this.order.digitalItems : []);
        this.fulfilledOrderItemsQuantity = quantity;
      } else {
        this.unfulfilledOrderItems = this.orderItems.concat(this.order.digitalItems ? this.order.digitalItems : []);
        this.unfulfilledOrderItemsQuantity = quantity;
      }
    }
    if (this.accessoryItemSku) {
      this.enableAccessory = true;
      this.unfulfilledOrderItems.sort((a, b) => (a.sku === this.accessoryItemSku ? -1 : 1));
    }
  }

  openPackageScanDialog() {
    const scanPackageDialog = this.dialog.open(FulfillScanPackageDialog, {
      minHeight: '156px',
      minWidth: '612px',
      panelClass: 'overflow-visible-class',
      autoFocus: false,
      data: {
        order: this.order,
        scannedSku: this.scannedSku,
      },
    });
    scanPackageDialog.afterClosed().subscribe(({ fulfilled, updatedBox }) => {
      if (fulfilled) {
        if (updatedBox) {
          this.packageName = this.boxService.utils.getBoxName(updatedBox);
        }
        this.fulfillAll(true);
      } else {
        this.scanInput.focus();
      }
    });
  }

  scanAccessory() {
    if (this.isOnlyAccessoryItem) {
      this.fulfillAll(true);
      return;
    }
    this.scannedAccessory = this.scanAccessoryInput?.input?.nativeElement?.value?.toLowerCase();
    const matchingOrderItem = this.scanItem(this.scannedAccessory, true, this.scanAccessoryInput);
    if (matchingOrderItem) {
      this.enableAccessory = false;
      this.scanInput.focus();
    }
    this.scannedAccessory = '';
    this.scanAccessoryInput.input.nativeElement.value = '';
  }

  scanMultiItemSku() {
    this.scannedMultiItemSku = this.scanMultiItemsSkuInput?.input?.nativeElement?.value?.toLowerCase();
    const matchingOrderItem = this.scanItem(this.scannedMultiItemSku, false, this.scanMultiItemsSkuInput);
    if (matchingOrderItem) {
      const targetSku = matchingOrderItem.orderItem?.sku;
      if (!this.multiItemScanned[targetSku]) {
        this.multiItemScanned[targetSku] = 0;
      }

      const totalQuantity = this.itemCards
        .filter((card: any) => card.orderItem?.sku === targetSku)
        .reduce((sum, card: any) => sum + card.orderItem.quantity, 0);

      if (this.multiItemScanned[targetSku] < totalQuantity) {
        this.multiItemScanned[targetSku]++;
      }
      if (this.multiItemScanned[targetSku] == totalQuantity) {
        const itemsToScanAmount = this.orderItems.reduce((acc, item) => acc + item.quantity, 0);
        const totalScannedItems = Object.values(this.multiItemScanned).reduce((acc, count) => acc + count, 0);
        if (totalScannedItems === itemsToScanAmount) {
          this.scanInput.focus();
          this.isMultiItemPending = false;
        }
      }
    }
    this.scannedMultiItemSku = '';
    this.scanMultiItemsSkuInput.input.nativeElement.value = '';
  }
}
